import { useCallback, useEffect, useMemo } from 'react';
import useAsync from 'react-use/lib/useAsync';

import { EntityFilterQuery } from '@backstage/catalog-client';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';

import { RepoNamePickerState } from './types';
import { serializeGroupEntityName } from './utils';

export const EntityGroupPicker = (props: {
  rawErrors: string[];
  state: RepoNamePickerState;
  onChange: (state: RepoNamePickerState) => void;
}) => {
  const { rawErrors, state, onChange } = props;

  const { entityRef } = state;

  const catalogApi = useApi(catalogApiRef);

  const catalogFilter: EntityFilterQuery = {
    kind: 'Group',
  };
  const { value: entities, loading } = useAsync(() =>
    catalogApi.getEntities({ filter: catalogFilter }),
  );

  const entityRefs = entities?.items.map(e => serializeGroupEntityName(e));
  const titles = useMemo(
    () =>
      new Map(
        entities?.items.map(e => [
          serializeGroupEntityName(e),
          e.metadata.title ?? e.metadata.name,
        ]),
      ),
    [entities],
  );
  const onSelect = useCallback(
    (_: any, value: string | null) => {
      onChange({ entityRef: value ?? undefined, repoName: '' });
    },
    [onChange],
  );

  useEffect(() => {
    if (entityRefs?.length === 1) {
      onChange({ entityRef: entityRefs[0] ?? undefined });
    }
  }, [entityRefs, onChange]);

  return (
    <FormControl
      variant="standard"
      margin="normal"
      required
      error={rawErrors?.length > 0}
    >
      <Autocomplete
        disabled={entityRefs?.length === 1}
        value={entityRef || ''}
        loading={loading}
        onChange={onSelect}
        options={entityRefs || []}
        getOptionLabel={option => titles.get(option) ?? option}
        autoSelect
        size="small"
        renderInput={params => (
          <TextField
            {...params}
            label="Team"
            margin="dense"
            helperText="Please choose your team this repo will belong to"
            slotProps={{ formHelperText: { style: { marginLeft: 0 } } }}
            variant="outlined"
            required
          />
        )}
      />
    </FormControl>
  );
};
